import { NotificationGroup } from '@/shared/types/message';
import type { NotificationIntent } from '@/modules/Notification';
import type { AnimationAlert } from '@/shared/types/alert';
import { cn } from '@/shared/utils/cn';
import {
  STACK_CHROME_TRANSITION,
  STACK_EXPAND_EASE,
  STACK_OFFSET_EXPANDED_PX,
  STACK_OFFSET_PX,
  STACK_PREVIEW_MAX_VISIBLE,
} from './notificationItemStackLayout';
import { useNotificationItemDeck } from '../../hooks/useNotificationItemDeck';
import { NotificationItemFullListPanel } from './NotificationItemFullListPanel';
import { NotificationItemStackDeck } from './NotificationItemStackDeck';

/** Grupo activo del servicio de notificaciones (cola, color de mazo, `isLeaving`, etc.). */
export interface NotificationItemProps {
  /** Estado del mazo: cola, contador, progreso de temporizador y bandera de salida animada. */
  group: NotificationGroup;
}

/**
 * Stack superpuesto anclado por la base: como máximo **3** notificaciones en vista previa;
 * con **hover** se muestran todas las de la cola. El desfase vertical aumenta al expandir.
 * Con hover, un clic fuera de controles interactivos abre la lista vertical con toda la cola.
 * La lista extendida no se cierra al salir el puntero del mazo (solo deja de contar como hover expandido).
 * Cerrar una notificación no debe colapsar el mazo ya extendido por hover (se suprime el mouseleave espurio).
 * Cierre **individual** por tarjeta inferior (`dismiss(id)`); cerrar la **frontal** (más reciente) vacía
 * todo el mazo (`dismissStack`). El **timer** (barra) solo en la más reciente; al expirar se cierra el grupo entero.
 * Con **hover** en el mazo (varias notas, vista **apilada**), el pie y la barra se atenúan con la misma
 * transición que el stack (sin `display: none`), sin pausar el temporizador del servicio. Tras abrir la
 * **lista completa** (clic en el grupo), el hover ya no atenúa la barra de progreso; el pie de total
 * («N notificaciones más» o `stackSummaryLegend` en `notify.message`) no se muestra en esa vista. Al expirar el temporizador de la más reciente, se vacía el mazo.
 * La cola del servicio mantiene **antigua → reciente**; la **vista** apila y lista **reciente → antigua**
 * (la más nueva al frente / arriba en la lista). Al cerrar una, las demás suben de orden; al pasar
 * `stackMax`, se expulsa la más antigua. El estrechamiento horizontal sigue la profundidad visual
 * (2ª = menos profunda / menos recorte; 3ª = más profunda / más recorte; al hover, 100%).
 * Con **más de una** notificación en el mazo (vista apilada), las alertas usan `animation="none"` y anulan
 * `transition-all` del `Alert` (`!ui-transition-none`) para no interpolar opacidad/layout sobre capas inferiores.
 * El listado (`NotificationPositions`) usa una `key` estable por color para no remontar
 * todo el mazo al entrar o salir notificaciones; el grupo se muta in-place en el servicio, así que este
 * componente no debe envolverse en `React.memo` por referencia de `group` (bloquearía la cola en pantalla).
 *
 * Tras cada cambio de cola se aplica un breve “asentamiento” (ver `STACK_LAYOUT_SETTLE_MS`) sin
 * transición de **padding** en el contenedor del mazo (evita solape con clics muy rápidos). Las capas
 * conservan transición en `bottom` para que el hover expanda siempre con animación, incluso justo
 * después de mostrar el stack.
 *
 * @example
 * import { NotificationItem } from '@imj_media/ui';
 *
 * <NotificationItem group={notificationGroupFromService} />
 */
export const NotificationItem = ({ group }: NotificationItemProps) => {
  const { queue, count, isLeaving, color } = group;
  const {
    deckHoverRef,
    isVisible,
    isStackExpanded,
    isFullListOpen,
    listMotionEnter,
    queueLayoutSettling,
    showTimer,
    notificationDuration,
    handleDismissOne,
    handleStackOpenListCapture,
    handleDeckMouseLeave,
    handleDeckMouseEnter,
    hideStackHoverChrome,
  } = useNotificationItemDeck(group);

  if (!isVisible || queue.length === 0 || !queue[0]?.id) {
    if (process.env.NODE_ENV !== 'production' && queue.length > 0 && !queue[0]?.id) {
      // eslint-disable-next-line no-console
      console.warn('[NotificationItem] Oldest notification without id. Skipping render.');
    }
    return null;
  }

  const previewLen = Math.min(STACK_PREVIEW_MAX_VISIBLE, queue.length);
  const visibleSlice = isStackExpanded ? queue : queue.slice(-previewLen);
  /** Reciente → antigua para frontal y lista; el servicio sigue en antigua → reciente. */
  const renderQueue = [...visibleSlice].reverse();
  /** Capas bajo la frontal: de más antigua (más profunda) a más reciente (pegada al frente). */
  const stackUnderLayers = renderQueue.length > 1 ? [...renderQueue.slice(1)].reverse() : [];
  const stackMultiVisible = renderQueue.length > 1;

  const stackDeckAlertClassName = cn(
    'ui-w-full !ui-transition-none !ui-duration-0 ui-opacity-100 motion-reduce:!ui-transition-none'
  );

  const newestId = queue[queue.length - 1]?.id;
  const frontPreviewId = renderQueue[0]?.id;
  const newestInStackPreview = newestId !== undefined && renderQueue.some((n) => n.id === newestId);

  const timerDurationInStack = (id: number | undefined) => {
    if (!showTimer || id === undefined || id !== newestId || !newestInStackPreview)
      return undefined;
    const entry = queue.find((n) => n.id === id);
    return entry?.duration ?? notificationDuration;
  };

  const intent: NotificationIntent = color;

  const perLayerBottomPx = isStackExpanded ? STACK_OFFSET_EXPANDED_PX : STACK_OFFSET_PX;
  const deckPaddingBottom = isFullListOpen ? 0 : stackUnderLayers.length * perLayerBottomPx;

  const stackedExtraCount = count > 1 ? count - 1 : 0;
  const stackSummaryLabel =
    group.stackSummaryLegend !== undefined
      ? group.stackSummaryLegend
      : stackedExtraCount === 1
        ? '1 notificación más'
        : `${stackedExtraCount} notificaciones más`;

  const stackSummaryInner =
    count > 1 ? (
      <div
        className="ui-mb-2 ui-mt-6 ui-flex ui-w-full ui-min-w-0 ui-items-center ui-justify-center ui-px-4"
        aria-label={stackSummaryLabel}
      >
        <span className="ui-min-w-0 ui-flex-1 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-center ui-text-body-xs-regular ui-font-body-xs-regular ui-text-tertiary">
          {stackSummaryLabel}
        </span>
      </div>
    ) : null;

  const stackListFooter =
    stackSummaryInner !== null ? (
      <div
        data-testid="notification-stack-footer-chrome"
        className={cn(
          'ui-overflow-hidden',
          STACK_CHROME_TRANSITION,
          hideStackHoverChrome
            ? 'ui-pointer-events-none ui-max-h-0 ui-opacity-0'
            : 'ui-max-h-40 ui-opacity-100'
        )}
        aria-hidden={hideStackHoverChrome}
      >
        {stackSummaryInner}
      </div>
    ) : null;

  const progressBarClassWhenStackHover = (durationMs: number | undefined) => {
    if (durationMs === undefined || durationMs <= 0) return undefined;
    return cn(
      'ui-overflow-hidden',
      STACK_CHROME_TRANSITION,
      hideStackHoverChrome && showTimer
        ? 'ui-pointer-events-none ui-max-h-0 ui-opacity-0'
        : 'ui-max-h-20 ui-opacity-100'
    );
  };

  return (
    <div
      ref={deckHoverRef}
      className={cn(
        'ui-overflow-visible',
        'ui-transition-[padding-bottom]',
        STACK_EXPAND_EASE,
        queueLayoutSettling && '!ui-transition-none'
      )}
      style={{ paddingBottom: deckPaddingBottom }}
      onMouseEnter={handleDeckMouseEnter}
      onMouseLeave={handleDeckMouseLeave}
    >
      <div
        className={cn(
          'ui-relative ui-w-[372px] ui-max-w-full ui-shrink-0',
          stackMultiVisible && 'ui-isolate'
        )}
        role="group"
        aria-expanded={isStackExpanded || isFullListOpen}
        aria-label="Pila de notificaciones"
        onClickCapture={isFullListOpen ? undefined : handleStackOpenListCapture}
      >
        {isFullListOpen ? (
          <NotificationItemFullListPanel
            queue={[...queue].reverse()}
            timerTargetId={newestId}
            intent={intent}
            isLeaving={isLeaving}
            listMotionEnter={listMotionEnter}
            showTimer={showTimer}
            notificationDuration={notificationDuration}
            progressBarClassWhenStackHover={progressBarClassWhenStackHover}
            handleDismissOne={handleDismissOne}
          />
        ) : (
          <NotificationItemStackDeck
            stackUnderLayers={stackUnderLayers}
            frontNotification={renderQueue[0]}
            frontNotificationKey={frontPreviewId ?? newestId}
            perLayerBottomPx={perLayerBottomPx}
            isStackExpanded={isStackExpanded}
            intent={intent}
            isLeaving={isLeaving}
            stackDeckAlertClassName={stackDeckAlertClassName}
            timerDurationInStack={timerDurationInStack}
            progressBarClassWhenStackHover={progressBarClassWhenStackHover}
            handleDismissOne={handleDismissOne}
            stackListFooter={stackListFooter}
          />
        )}
      </div>
    </div>
  );
};

NotificationItem.displayName = 'NotificationItem';
